import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RecipientCard = ({ recipient, onEdit, onDelete, onSendMoney, onViewDetails }) => {
  const getVerificationStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'failed':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getVerificationIcon = (status) => {
    switch (status) {
      case 'verified':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'XCircle';
      default:
        return 'AlertCircle';
    }
  };

  const formatLastUsed = (date) => {
    const now = new Date();
    const lastUsed = new Date(date);
    const diffInDays = Math.floor((now - lastUsed) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return lastUsed?.toLocaleDateString('en-GB');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="User" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{recipient?.name}</h3>
            <p className="text-sm text-muted-foreground">{recipient?.relationship}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(recipient)}
            iconName="Edit"
            iconSize={16}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(recipient)}
            iconName="Trash2"
            iconSize={16}
            className="text-error hover:text-error"
          />
        </div>
      </div>
      {/* Bank Details */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Building" size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">{recipient?.bankName}</span>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">IBAN</p>
            <p className="text-sm font-mono text-foreground">{recipient?.iban}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">SWIFT/BIC</p>
            <p className="text-sm font-mono text-foreground">{recipient?.swiftCode}</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Image
              src={recipient?.countryFlag}
              alt={`${recipient?.country} flag`}
              className="w-5 h-4 rounded-sm object-cover"
            />
            <span className="text-sm text-foreground">{recipient?.country}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            {recipient?.supportedCurrencies?.map((currency) => (
              <span
                key={currency}
                className="px-2 py-1 text-xs font-medium bg-muted text-muted-foreground rounded"
              >
                {currency}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Verification Status */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onViewDetails(recipient)}
          className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${getVerificationStatusColor(recipient?.verificationStatus)}`}
        >
          <Icon name={getVerificationIcon(recipient?.verificationStatus)} size={12} />
          <span className="capitalize">{recipient?.verificationStatus}</span>
        </button>
        
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Last used</p>
          <p className="text-sm text-foreground">{formatLastUsed(recipient?.lastUsed)}</p>
        </div>
      </div>
      {/* Actions */}
      <div className="flex space-x-2">
        <Button
          variant="default"
          size="sm"
          onClick={() => onSendMoney(recipient)}
          iconName="Send"
          iconPosition="left"
          iconSize={16}
          className="flex-1"
        >
          Send Money
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(recipient)}
          iconName="Eye"
          iconSize={16}
        />
      </div>
    </div>
  );
};

export default RecipientCard;